import { ClockCircleOutlined } from "@ant-design/icons"
import React from "react"
import { useTranslation } from "react-i18next"

import {
  type HoldInfo,
  type SendTimeForecast,
  formatInZone,
  holdZoneSource,
  sameClock
} from "./held-until.ts"

/**
 * When a held message goes out, always with the zone it is read in and where
 * that zone came from; never in the sender's own zone alone.
 */
export const HoldNotice = ({
  hold,
  accountZone,
  className = "block text-sm text-slate-600"
}: {
  hold: HoldInfo
  accountZone?: string
  className?: string
}) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || "en"
  const time = formatInZone(hold.not_before, hold.hold_zone, locale)
  if (!time || !hold.not_before || !hold.hold_zone) return null
  const source = holdZoneSource(hold)
  const basis = t(`communication.holdNotice.basis.${source}`, {
    zone: hold.hold_zone
  })
  const yours =
    accountZone &&
    !sameClock(hold.not_before, hold.hold_zone, accountZone, locale)
      ? formatInZone(hold.not_before, accountZone, locale)
      : null
  return (
    <span className={className}>
      <ClockCircleOutlined aria-hidden className="mr-1" />
      {t("communication.holdNotice.goesAt", { time, basis })}
      {yours && (
        <> ({t("communication.holdNotice.yourTime", { time: yours })})</>
      )}
    </span>
  )
}

/** Beside an eligible recipient: whether a message sent now would wait, why, and until when. */
export const SendTimeNote = ({
  forecast,
  accountZone
}: {
  forecast: SendTimeForecast | undefined
  accountZone?: string
}) => {
  const { t } = useTranslation()
  if (!forecast?.held || !forecast.hold_reason) return null
  return (
    <span className="mt-1 flex flex-col text-xs text-amber-800">
      <span>
        {t("communication.holdNotice.forecast", {
          reason: t(`communication.composer.reasons.${forecast.hold_reason}`, {
            defaultValue: forecast.hold_reason
          })
        })}
      </span>
      <HoldNotice
        hold={forecast}
        accountZone={accountZone}
        className="text-xs text-slate-600"
      />
    </span>
  )
}

export default HoldNotice
